import { useCallback, useEffect, useState } from 'react';
import type { Importacion, ListaImportaciones } from '../../../api/ops/clientesImportar';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Chip } from '../../../componentes/Chip';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { Paginacion } from '../../../componentes/Paginacion';
import { rolesDe } from '../../../logica/acceso';
import { estadoDeError } from '../../../logica/estados';
import { diaYHora, kilobytes } from '../../../logica/fechasCortas';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import { textoDeError } from '../../equipo/textos';
import type { PropsPantalla } from '../../tipos';
import { PanelImportar, type AperturaImportar } from './PanelImportar';
import { descargarPlantilla } from './plantilla';
import { AYUDA, ESTADO, PERMISO_IMPORTAR, TEXTOS, lineaResumen } from './textos';
import '../../../componentes/apartado.css';
import './importar.css';

/**
 * Importar clientes (2.6): las importaciones (también la migración desde la v1) con su archivo, quién la subió (G149), sus
 * permisos y su resultado; las descartadas, «Descartada». El proceso va en RV Panel con RV Pasos: Archivo · Permisos ·
 * Revisar. Importar nunca lleva «Deshacer» (G159). Solo con `clientes.importaciones.gestionar`.
 */
export function Importar(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const gestionar = permisos.puede(PERMISO_IMPORTAR);
    const importar = gestionar && !soloLectura;
    const pag = usePaginacionCursor();
    const [cargada, setCargada] = useState<{ cursor: string | null; lista: ListaImportaciones | null; error: unknown } | null>(null);
    const [intento, setIntento] = useState(0);
    const [abierta, setAbierta] = useState<AperturaImportar | null>(null);
    const [hecho, setHecho] = useState<string | null>(null);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!gestionar) {
            return;
        }
        let vivo = true;
        const cursor = pag.cursor;
        ops.listCustomerImports(negocio, { cursor })
            .then((l) => {
                if (vivo) {
                    setCargada({ cursor, lista: l, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCargada((c) => ({ cursor, lista: c?.cursor === cursor ? c.lista : null, error: e }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, pag.cursor, intento, gestionar]);

    const plantilla = () => {
        descargarPlantilla(ops, negocio, membresia?.slug).catch((e: unknown) => {
            avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido descargar la plantilla. Prueba de nuevo.') });
        });
    };

    const acciones: AccionCabecera[] = [];
    if (gestionar) {
        acciones.push({ clave: 'plantilla', texto: 'Descargar plantilla', icono: 'download', secundaria: true, onClick: plantilla });
        acciones.push({ clave: 'importar', texto: 'Importar clientes', icono: 'upload', desactivado: soloLectura, onClick: () => { setHecho(null); setAbierta({ tipo: 'nueva' }); } });
    }
    useCabecera({ ayuda: gestionar ? AYUDA.lista : [], acciones });

    if (!gestionar) {
        return <EstadoSinPermiso seccion="Importar clientes" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    const lista = cargada?.cursor === pag.cursor ? cargada.lista : null;
    const error = cargada?.cursor === pag.cursor ? cargada.error : null;
    if (error !== null && lista === null) {
        return <EstadoError seccion="tus importaciones" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (lista === null) {
        return <EstadoCargando seccion="importaciones" />;
    }

    const ahora = new Date();
    const fila = (i: Importacion) => {
        const migracion = i.source === 'migration_v1';
        const titulo = migracion ? 'Migración desde la v1' : (i.file_name ?? 'Importación');
        const sub = migracion ? `${diaYHora(i.created_at, ahora)} · la hizo Reservaria` : [diaYHora(i.created_at, ahora), kilobytes(i.file_size), i.created_by_name ?? ''].filter((t) => t !== '').join(' · ');
        const declarada = i.consent_evidence === 'declared';
        const [chip, tono] = ESTADO[i.status];
        return (
            <div
                key={i.id}
                role="listitem"
                className={`rv-ca-fila rv-ca-fila--abre rv-imp-fila${movil ? ' rv-imp-fila--movil' : ''}`}
                tabIndex={0}
                aria-label={`${titulo}: ${lineaResumen(i)}`}
                onClick={() => { setHecho(null); setAbierta({ tipo: 'ver', importacion: i }); }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        setAbierta({ tipo: 'ver', importacion: i });
                    }
                }}
            >
                <span className="rv-imp-archivo">
                    <span className="rv-ca-baldosa">
                        <Icono nombre={migracion ? 'history' : 'file-spreadsheet'} />
                    </span>
                    <span className="rv-ca-fila__texto">
                        <strong>{titulo}</strong>
                        <span>{sub}</span>
                        {movil && <span>{lineaResumen(i)}</span>}
                    </span>
                </span>
                {!movil && (
                    <>
                        <span className="rv-imp-permisos">
                            <Icono nombre={declarada ? 'shield-check' : 'shield'} />
                            {declarada ? TEXTOS.conPrueba : TEXTOS.soloServicio}
                        </span>
                        <span className="rv-imp-resultado">{lineaResumen(i)}</span>
                    </>
                )}
                <span className="rv-imp-estado">{i.status !== 'completed' && <Chip estado={tono}>{chip}</Chip>}</span>
                <Icono nombre="chevron-right" className="rv-ca-chevron" />
            </div>
        );
    };

    return (
        <div className="rv-ca-apartado">
            {hecho !== null && (
                <div className="rv-ca-hecho" role="status">
                    <span className="rv-ca-hecho__baldosa">
                        <Icono nombre="check" />
                    </span>
                    <span>{hecho}</span>
                </div>
            )}
            {soloLectura && (
                <p className="rv-ca-nota">
                    <Icono nombre="eye" />
                    <span>{TEXTOS.soloLectura}</span>
                </p>
            )}
            {lista.data.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre="inbox" />
                    </span>
                    <strong>{TEXTOS.vacia}</strong>
                    <span>{TEXTOS.vaciaTexto}</span>
                </div>
            ) : (
                <>
                    <div className="rv-ca-lista" role="list" aria-label="Importaciones">
                        {!movil && (
                            <div className="rv-imp-cab" aria-hidden="true">
                                <span>Importación</span>
                                <span>Permisos</span>
                                <span>Resultado</span>
                                <span>Estado</span>
                            </div>
                        )}
                        {lista.data.map(fila)}
                    </div>
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
                </>
            )}
            <PanelImportar
                ops={ops}
                negocio={negocio}
                movil={movil}
                apertura={abierta}
                puedeImportar={importar}
                onPlantilla={plantilla}
                onCerrar={() => { setAbierta(null); }}
                onCambio={recargar}
                onDescartada={() => {
                    setAbierta(null);
                    setHecho(TEXTOS.descartadaHecho);
                    recargar();
                }}
                onIr={navegar}
            />
        </div>
    );
}
